import { Component, Input } from "@angular/core";
import { CommonModule } from "@angular/common";
import { SkeletonModule } from "primeng/skeleton";

export interface SkeletonColumnConfig {
  width: string;
  height?: string;
  shape?: "rectangle" | "circle";
  align?: "left" | "center" | "right";
}

/**
 * Reusable skeleton rows component for use inside p-table loadingbody templates.
 * Generates clean rectangular skeleton bars matching the customers table style.
 *
 * Usage:
 * ```html
 * <ng-template pTemplate="loadingbody">
 *   <app-table-skeleton-rows [rows]="5" [columns]="skeletonColumns" />
 * </ng-template>
 * ```
 */
@Component({
  selector: "app-table-skeleton-rows",
  standalone: true,
  imports: [CommonModule, SkeletonModule],
  template: `
    @for (row of rowsArray; track row) {
      <tr class="skeleton-row">
        @for (col of columns; track $index) {
          <td
            [class.text-center]="col.align === 'center'"
            [class.text-right]="col.align === 'right'"
          >
            <p-skeleton
              [width]="col.width"
              [height]="col.height ?? '1rem'"
              [shape]="col.shape === 'circle' ? 'circle' : 'rectangle'"
            />
          </td>
        }
      </tr>
    }
  `,
  styles: [
    `
      :host {
        display: contents;
      }

      .skeleton-row td {
        padding: 0.75rem 1rem;
      }
    `,
  ],
})
export class TableSkeletonRowsComponent {
  @Input() rows: number = 5;
  @Input() columns: SkeletonColumnConfig[] = [];

  get rowsArray(): number[] {
    return Array.from({ length: this.rows }, (_, i) => i + 1);
  }
}

/**
 * Preset column configurations for common table types
 */
export const SKELETON_PRESETS = {
  /** Customers table skeleton columns */
  customers: [
    {
      width: "1.5rem",
      height: "1.5rem",
      shape: "circle" as const,
      align: "center" as const,
    },
    { width: "2rem", height: "1rem" },
    { width: "120px", height: "1rem" },
    { width: "180px", height: "1rem" },
    { width: "60px", height: "1.5rem", align: "center" as const },
    { width: "40px", height: "1.5rem", align: "center" as const },
    { width: "70px", height: "1rem", align: "right" as const },
    { width: "80px", height: "1rem" },
    { width: "80px", height: "1rem" },
    {
      width: "1.5rem",
      height: "1.5rem",
      shape: "circle" as const,
      align: "center" as const,
    },
  ],

  /** Products table skeleton columns */
  products: [
    {
      width: "1.5rem",
      height: "1.5rem",
      shape: "circle" as const,
      align: "center" as const,
    },
    { width: "1.5rem", height: "1.5rem", align: "center" as const },
    { width: "3rem", height: "1rem" },
    { width: "120px", height: "1rem" },
    { width: "50px", height: "1.5rem" },
    { width: "70px", height: "1.5rem" },
    { width: "50px", height: "1rem" },
    { width: "60px", height: "1rem" },
    { width: "30px", height: "1rem" },
    { width: "130px", height: "1rem" },
    { width: "30px", height: "1rem", align: "center" as const },
    { width: "30px", height: "1rem", align: "center" as const },
    { width: "30px", height: "1rem", align: "center" as const },
    {
      width: "1.5rem",
      height: "1.5rem",
      shape: "circle" as const,
      align: "center" as const,
    },
    {
      width: "1.5rem",
      height: "1.5rem",
      shape: "circle" as const,
      align: "center" as const,
    },
  ],

  /** Scheduled orders table skeleton columns */
  scheduledOrders: [
    {
      width: "1.5rem",
      height: "1.5rem",
      shape: "circle" as const,
      align: "center" as const,
    },
    { width: "1.5rem", height: "1.5rem", align: "center" as const },
    { width: "3rem", height: "1rem" },
    { width: "70px", height: "1.5rem" },
    {
      width: "1.5rem",
      height: "1.5rem",
      shape: "circle" as const,
      align: "center" as const,
    },
    { width: "1.5rem", height: "1.5rem", align: "center" as const },
    { width: "120px", height: "1rem" },
    { width: "160px", height: "1rem" },
    { width: "70px", height: "1rem" },
    { width: "80px", height: "1rem" },
    { width: "80px", height: "1rem" },
    { width: "40px", height: "1rem", align: "center" as const },
    { width: "60px", height: "1rem" },
    {
      width: "1.5rem",
      height: "1.5rem",
      shape: "circle" as const,
      align: "center" as const,
    },
  ],

  /** Coupons table skeleton columns */
  coupons: [
    {
      width: "1.5rem",
      height: "1.5rem",
      shape: "circle" as const,
      align: "center" as const,
    },
    { width: "2rem", height: "1rem" },
    { width: "100px", height: "1rem" },
    { width: "120px", height: "1rem" },
    { width: "80px", height: "1.5rem", align: "center" as const },
    { width: "60px", height: "1rem" },
    { width: "50px", height: "1rem" },
    { width: "40px", height: "1rem" },
    { width: "40px", height: "1rem" },
    { width: "40px", height: "1rem" },
    { width: "60px", height: "1rem" },
    { width: "40px", height: "1rem" },
    { width: "40px", height: "1rem" },
    { width: "80px", height: "1rem" },
    {
      width: "1.5rem",
      height: "1.5rem",
      shape: "circle" as const,
      align: "center" as const,
    },
  ],

  /** Retention workflows table skeleton columns */
  retentionWorkflows: [
    {
      width: "1.5rem",
      height: "1.5rem",
      shape: "circle" as const,
      align: "center" as const,
    },
    { width: "3rem", height: "1rem" },
    { width: "150px", height: "1rem" },
    { width: "60px", height: "1.5rem" },
    { width: "40px", height: "1.25rem", align: "center" as const },
    { width: "70px", height: "1rem" },
    { width: "70px", height: "1rem" },
    { width: "70px", height: "1rem" },
    {
      width: "1.5rem",
      height: "1.5rem",
      shape: "circle" as const,
      align: "center" as const,
    },
  ],

  /** Quick links table skeleton columns */
  quickLinks: [
    {
      width: "1.5rem",
      height: "1.5rem",
      shape: "circle" as const,
      align: "center" as const,
    },
    { width: "3rem", height: "1rem" },
    { width: "120px", height: "1rem" },
    { width: "100px", height: "1rem" },
    { width: "80px", height: "1rem" },
    { width: "70px", height: "1.5rem" },
    { width: "40px", height: "1.25rem" },
    { width: "80px", height: "1rem" },
    {
      width: "1.5rem",
      height: "1.5rem",
      shape: "circle" as const,
      align: "center" as const,
    },
  ],

  /** Product groups table skeleton columns (with expand and products count) */
  productGroups: [
    {
      width: "1.5rem",
      height: "1.5rem",
      shape: "circle" as const,
      align: "center" as const,
    }, // Expand toggle
    { width: "3rem", height: "1rem" }, // ID
    { width: "150px", height: "1rem" }, // Name
    { width: "2rem", height: "1.25rem", align: "center" as const }, // Products count
    { width: "80px", height: "1rem" }, // Created
    { width: "80px", height: "1rem" }, // Updated
    {
      width: "1.5rem",
      height: "1.5rem",
      shape: "circle" as const,
      align: "center" as const,
    }, // Actions menu
  ],

  /** Payment integrations table skeleton columns */
  paymentIntegrations: [
    { width: "3rem", height: "1rem" }, // ID
    { width: "120px", height: "1rem" }, // Provider
    { width: "140px", height: "1rem" }, // API Account
    { width: "140px", height: "1rem" }, // API Key
    { width: "50px", height: "1.5rem" }, // Mode tag
    { width: "80px", height: "1rem" }, // Created
    {
      width: "1.5rem",
      height: "1.5rem",
      shape: "circle" as const,
      align: "center" as const,
    }, // Actions menu
  ],
};
